import { Injectable } from '@angular/core';
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
} from '@microsoft/signalr';
import { BehaviorSubject, NEVER, Observable, Subject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { environment } from '@env/environment';
import { CredentialsService } from './credentials.service';

export interface ResourceUpdatedSignal {
  group: string;
  correlationId: string;
}

/**
 * `connecting` — establishing the initial connection, no hub connection has
 * ever succeeded yet.
 * `connected` — live.
 * `reconnecting` — a previously-connected connection dropped and SignalR is
 * retrying automatically.
 * `disconnected` — the connection closed and is not currently retrying
 * (initial connect failed, or automatic reconnect gave up).
 */
export type LiveConnectionState =
  'connecting' | 'connected' | 'reconnecting' | 'disconnected';

const RESOURCE_HUB_URL = `${environment.signalRUrl}/customer/customers-hub`;
const RESOURCE_UPDATED_EVENT = 'ResourceUpdated';
const JOIN_METHOD = 'JoinResourceGroup';
const LEAVE_METHOD = 'LeaveResourceGroup';

/**
 * Shared entry point for the backend's generic real-time update system
 * (see FlyPlusBackend DOCS/RealTime-LiveUpdate-DeveloperGuide.md).
 *
 * Any page can call `subscribe(subscriptions)` with the group names returned
 * by its API response; it will re-emit whenever the backend reports a change
 * to one of those groups. One physical hub connection is shared across the
 * whole app and groups are ref-counted, so multiple pages/components
 * watching the same group don't join/leave the hub group more than once.
 */
@Injectable({ providedIn: 'root' })
export class LiveUpdateService {
  private hubConnection: HubConnection | null = null;
  private connectionStart: Promise<void> | null = null;
  private readonly signal$ = new Subject<ResourceUpdatedSignal>();
  private readonly groupRefCounts = new Map<string, number>();
  private readonly connectionStateSubject =
    new BehaviorSubject<LiveConnectionState>('connecting');

  /** Current hub connection status; drives status indicators like LiveStatusIndicatorComponent. */
  readonly connectionState$: Observable<LiveConnectionState> =
    this.connectionStateSubject.asObservable();

  constructor(private credentialsService: CredentialsService) {}

  /**
   * Subscribe to one or more resource groups. Emits a `ResourceUpdatedSignal`
   * whenever the backend reports a change to any of the given groups.
   * Unsubscribing (or the component being destroyed via `untilDestroyed`)
   * automatically leaves the groups once nobody else is watching them.
   */
  subscribe(groups: string[]): Observable<ResourceUpdatedSignal> {
    const activeGroups = Array.from(new Set((groups ?? []).filter(Boolean)));
    if (!activeGroups.length) {
      return NEVER;
    }

    activeGroups.forEach((group) => this.joinGroup(group));

    return new Observable<ResourceUpdatedSignal>((subscriber) => {
      const subscription = this.signal$
        .pipe(filter((signal) => activeGroups.includes(signal.group)))
        .subscribe(subscriber);

      return () => {
        subscription.unsubscribe();
        activeGroups.forEach((group) => this.leaveGroup(group));
      };
    });
  }

  private ensureConnected(): Promise<void> {
    if (this.hubConnection?.state === HubConnectionState.Connected) {
      return Promise.resolve();
    }
    if (this.connectionStart) {
      return this.connectionStart;
    }

    const connection = new HubConnectionBuilder()
      .withUrl(RESOURCE_HUB_URL, {
        accessTokenFactory: () =>
          this.credentialsService.credentials?.token ?? '',
      })
      .withAutomaticReconnect()
      .build();

    connection.on(RESOURCE_UPDATED_EVENT, (signal: ResourceUpdatedSignal) => {
      this.signal$.next(signal);
    });

    connection.onreconnecting(() =>
      this.connectionStateSubject.next('reconnecting'),
    );
    connection.onreconnected(() => {
      this.connectionStateSubject.next('connected');
      this.rejoinAllGroups();
    });
    connection.onclose(() => this.connectionStateSubject.next('disconnected'));

    this.connectionStateSubject.next('connecting');
    this.hubConnection = connection;
    this.connectionStart = connection
      .start()
      .then(() => {
        this.connectionStart = null;
        this.connectionStateSubject.next('connected');
      })
      .catch((err) => {
        this.connectionStart = null;
        this.connectionStateSubject.next('disconnected');
        throw err;
      });

    return this.connectionStart;
  }

  private joinGroup(group: string): void {
    const count = this.groupRefCounts.get(group) ?? 0;
    this.groupRefCounts.set(group, count + 1);
    if (count === 0) {
      this.invokeWhenConnected(JOIN_METHOD, group);
    }
  }

  private leaveGroup(group: string): void {
    const count = this.groupRefCounts.get(group) ?? 0;
    if (count <= 1) {
      this.groupRefCounts.delete(group);
      this.invokeWhenConnected(LEAVE_METHOD, group);
    } else {
      this.groupRefCounts.set(group, count - 1);
    }
  }

  private rejoinAllGroups(): void {
    this.groupRefCounts.forEach((_count, group) => {
      this.invokeWhenConnected(JOIN_METHOD, group);
    });
  }

  private invokeWhenConnected(method: string, group: string): void {
    this.ensureConnected()
      .then(() => this.hubConnection?.invoke(method, group))
      .catch(() => {
        // Connection failed to establish; the group will be re-joined
        // the next time a subscriber triggers ensureConnected(), or on
        // the next successful automatic reconnect.
      });
  }
}
